import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, SparklesIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { DefaultService, GamesService, GameOutput, GameUpdateInput, TagSuggestions, MetadataFieldDefinition, BGGGameDetails } from '../generated-api';
import { useGames } from '../contexts/DataContext';
import GameImageUpload from '../components/common/GameImageUpload';
import TagInput from '../components/common/TagInput';
import OwnerInput from '../components/common/OwnerInput';
import MetadataFieldsEditor from '../components/MetadataFieldsEditor';
import BGGSearchModal from '../components/BGGSearchModal';

const GameEditLibraryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { games, fetch: fetchGames, invalidate: invalidateGames } = useGames();
  const [game, setGame] = useState<GameOutput | null>(null);
  
  // Game fields
  const [name, setName] = useState<string>('');
  const [availableExtensions, setAvailableExtensions] = useState<string[]>([]);
  const [minTeams, setMinTeams] = useState<number>(2);
  const [maxTeams, setMaxTeams] = useState<number>(4);
  const [description, setDescription] = useState<string>('');
  const [extensionInput, setExtensionInput] = useState<string>('');
  const [owners, setOwners] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [availableOwners, setAvailableOwners] = useState<string[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [playerMetadataFields, setPlayerMetadataFields] = useState<MetadataFieldDefinition[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [bggModalOpen, setBggModalOpen] = useState(false);
  const [bggImageUrl, setBggImageUrl] = useState<string | null>(null);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  useEffect(() => {
    if (id && games.length > 0) {
      const foundGame = games.find((g) => g.id === id);
      if (foundGame) {
        setGame(foundGame);
        setName(foundGame.name);
        setAvailableExtensions(foundGame.availableExtensions || []);
        setMinTeams(foundGame.minTeams);
        setMaxTeams(foundGame.maxTeams);
        setDescription(foundGame.description || '');
        setOwners(foundGame.owners || []);
        setTags(foundGame.tags || []);
        setPlayerMetadataFields(foundGame.playerMetadataFields || []);
      }
      setLoading(false);
    } else if (games.length > 0) {
      setLoading(false);
    }
  }, [id, games]);

  // Fetch available tags on component mount
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const tags = await DefaultService.getTagsTagsGet();
        setAvailableTags(tags);
      } catch (err) {
        console.error('Failed to fetch tags:', err);
        // Don't show error to user for tags, it's not critical
      }
    };

    fetchTags();
  }, []);

  // Fetch available owners on component mount
  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const owners = await DefaultService.getOwnersOwnersGet();
        setAvailableOwners(owners);
      } catch (err) {
        console.error('Failed to fetch owners:', err);
        // Don't show error to user for owners, it's not critical
      }
    };

    fetchOwners();
  }, []);

  // Extension management
  const addExtension = () => {
    if (extensionInput.trim() && !availableExtensions.includes(extensionInput.trim())) {
      setAvailableExtensions([...availableExtensions, extensionInput.trim()]);
      setExtensionInput('');
    }
  };

  const removeExtension = (extensionToRemove: string) => {
    setAvailableExtensions(availableExtensions.filter(ext => ext !== extensionToRemove));
  };


  const getTagSuggestions = async () => {
    if (!id) return;
    
    setLoadingSuggestions(true);
    try {
      const suggestions: TagSuggestions = await DefaultService.getTagSuggestionsGamesGameIdTagSuggestionsGet({
        gameId: id
      });
      
      // Merge existing suggestions into availableTags if they're not already there
      const newExistingTags = suggestions.existing.filter(tag => !availableTags.includes(tag));
      const newSuggestedTags = suggestions.new.filter(tag => !availableTags.includes(tag));
      
      if (newExistingTags.length > 0 || newSuggestedTags.length > 0) {
        setAvailableTags(prev => [...prev, ...newExistingTags, ...newSuggestedTags]);
      }
      
      // Auto-select all suggested tags that aren't already selected
      const allSuggestions = [...suggestions.existing, ...suggestions.new];
      const tagsToAdd = allSuggestions.filter(tag => !tags.includes(tag));
      
      if (tagsToAdd.length > 0) {
        setTags(prev => [...prev, ...tagsToAdd]);
      }
    } catch (err) {
      console.error('Failed to get tag suggestions:', err);
      setError('Failed to get tag suggestions. Please try again.');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Handler for when a BGG game is selected
  const handleBggSelect = useCallback((details: BGGGameDetails) => {
    if (details.description) {
      setDescription(details.description);
    }

    if (details.minPlayers) {
      setMinTeams(details.minPlayers);
    }
    if (details.maxPlayers) {
      setMaxTeams(details.maxPlayers);
    }

    if (details.imageUrl) {
      setBggImageUrl(details.imageUrl);
      setImageFile(null);
    }

    // Use LLM-mapped tags if available, otherwise fall back to raw BGG categories+mechanics
    let combinedTags: string[] = [];

    if (details.mappedTags && details.mappedTags.length > 0) {
      combinedTags = details.mappedTags.slice(0, 10);
    } else {
      if (details.categories) {
        for (const category of details.categories) {
          if (combinedTags.length < 10) {
            combinedTags.push(category);
          }
        }
      }
      if (details.mechanics) {
        for (const mechanic of details.mechanics) {
          if (combinedTags.length < 10 && !combinedTags.includes(mechanic)) {
            combinedTags.push(mechanic);
          }
        }
      }
    }

    if (combinedTags.length > 0) {
      setTags(combinedTags);
      setAvailableTags(prev => {
        const newTags = combinedTags.filter(t => !prev.includes(t));
        return [...prev, ...newTags];
      });
    }

    if (details.expansions && details.expansions.length > 0) {
      setAvailableExtensions(details.expansions);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id) return;

    // Validation
    if (!name.trim()) {
      setError('Game name is required.');
      return;
    }

    if (minTeams < 1) {
      setError('Minimum teams must be at least 1.');
      return;
    }

    if (maxTeams < minTeams) {
      setError('Maximum teams must be greater than or equal to minimum teams.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Validate metadata fields before submission
      const validMetadataFields = playerMetadataFields.filter(field => 
        field.name.trim() && 
        field.displayName.trim() && 
        /^[a-zA-Z0-9_]+$/.test(field.name)
      );

      const gameUpdateInput: GameUpdateInput = {
        name: name.trim(),
        availableExtensions: availableExtensions,
        minTeams: minTeams,
        maxTeams: maxTeams,
        description: description.trim() || undefined,
        owners: owners.length > 0 ? owners : undefined,
        tags: tags.length > 0 ? tags : undefined,
        playerMetadataFields: validMetadataFields.length > 0 ? validMetadataFields : undefined
      };

      await DefaultService.updateGameGamesGameIdPut({
        gameId: id,
        requestBody: gameUpdateInput
      });
      
      // Upload image separately if provided
      if (imageFile) {
        await DefaultService.uploadGameImageGamesGameIdImagePost({
          gameId: id,
          formData: { image: imageFile }
        });
      } else if (bggImageUrl) {
        try {
          await GamesService.importBggImageGamesGameIdImportBggImagePost({
            gameId: id,
            imageUrl: bggImageUrl
          });
        } catch (imgErr) {
          console.error('Failed to import BGG image:', imgErr);
        }
      }
      // Invalidate games cache to ensure fresh data on next load
      invalidateGames();
      const returnPath = (location.state as { returnPath?: string })?.returnPath;
      navigate(returnPath || `/games/${id}`);
    } catch (err) {
      setError('Failed to update game. Please try again.');
      console.error('Error updating game:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-heading mb-4">Game Not Found</h2>
        <p className="text-body mb-6">The game you're trying to edit doesn't exist.</p>
        <Link
          to="/games"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-700 hover:bg-primary-800"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Game Library
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => {
            const returnPath = (location.state as { returnPath?: string })?.returnPath;
            navigate(returnPath || '/games');
          }}
          className="text-primary-700 hover:text-primary-800 mb-4 inline-block"
        >
          ← Back to Game Library
        </button>
        <div className="mb-4">
          <h1 className="text-fluid-h1 font-bold text-heading">Edit Game</h1>
          <p className="mt-2 text-body">
            Modify game details in your library
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="bg-surface shadow rounded-lg p-6 max-w-2xl">
          {/* Import from BGG Section */}
          <div className="mb-6 p-4 bg-primary-50 rounded-lg border border-primary-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-primary-900">Update from BGG</h3>
                <p className="text-sm text-primary-700 mt-1">
                  Pull in description, tags, image, and expansions from BoardGameGeek
                </p>
              </div>
              <button
                type="button"
                onClick={() => setBggModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <GlobeAltIcon className="h-4 w-4 mr-2" />
                Import from BGG
              </button>
            </div>
          </div>

          <h2 className="text-lg font-medium text-heading mb-6">Game Details</h2>
          
          <div className="space-y-6">
            {/* Game Image */}
            <div>
              <label className="block text-sm font-medium text-body mb-3 text-center">
                Game Cover Image
              </label>
              {bggImageUrl && !imageFile ? (
                <div className="mb-4">
                  <div className="relative w-48 h-48 mx-auto">
                    <img
                      src={bggImageUrl}
                      alt="BGG import"
                      className="w-full h-full object-cover rounded-lg shadow-md"
                    />
                    <button
                      type="button"
                      onClick={() => setBggImageUrl(null)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      title="Remove image"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-xs text-muted text-center mt-2">
                    Image from BoardGameGeek (will be saved to our servers)
                  </p>
                </div>
              ) : (
                <GameImageUpload
                  onImageChange={(file) => {
                    setImageFile(file);
                    if (file) {
                      setBggImageUrl(null);
                    }
                  }}
                  currentImageUrl={game?.imageUrl}
                  className="mb-4"
                />
              )}
            </div>

            {/* Game Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-body mb-2">
                Game Name *
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Catan, Splendor, Wingspan"
                required
              />
            </div>

            {/* Team Configuration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="minTeams" className="block text-sm font-medium text-body mb-2">
                  Minimum Teams *
                </label>
                <input
                  type="number"
                  id="minTeams"
                  value={minTeams}
                  onChange={(e) => setMinTeams(parseInt(e.target.value) || 1)}
                  className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  min="1"
                  required
                />
              </div>
              <div>
                <label htmlFor="maxTeams" className="block text-sm font-medium text-body mb-2">
                  Maximum Teams *
                </label>
                <input
                  type="number"
                  id="maxTeams"
                  value={maxTeams}
                  onChange={(e) => setMaxTeams(parseInt(e.target.value) || 1)}
                  className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  min="1"
                  required
                />
              </div>
            </div>

            {/* Available Extensions */}
            <div>
              <label htmlFor="extensionInput" className="block text-sm font-medium text-body mb-2">
                Available Extensions / Expansions
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  id="extensionInput"
                  value={extensionInput}
                  onChange={(e) => setExtensionInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExtension())}
                  className="flex-1 border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Add extension name"
                />
                <button
                  type="button"
                  onClick={addExtension}
                  className="px-4 py-2 bg-primary-700 text-white rounded-md hover:bg-primary-800 focus:ring-2 focus:ring-primary-500"
                >
                  Add
                </button>
              </div>
              {availableExtensions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {availableExtensions.map((extension, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-purple-100 text-purple-800"
                    >
                      {extension}
                      <button
                        type="button"
                        onClick={() => removeExtension(extension)}
                        className="ml-1 text-purple-600 hover:text-purple-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted mt-1">
                List all available extensions or expansions for this game
              </p>
            </div>

            {/* Owners */}
            <div>
              <label className="block text-sm font-medium text-body mb-2">
                Owners (Optional)
              </label>
              <OwnerInput
                owners={owners}
                availableOwners={availableOwners}
                onOwnersChange={setOwners}
                placeholder="Add owner (e.g., John, Mary, Alex...)"
                maxOwners={10}
              />
              <p className="text-xs text-muted mt-1">
                List who owns this game (useful for shared game collections)
              </p>
            </div>

            {/* Tags */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-body">
                  Tags (Optional)
                </label>
                <button
                  type="button"
                  onClick={getTagSuggestions}
                  disabled={loadingSuggestions}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <SparklesIcon className="h-4 w-4 mr-1" />
                  {loadingSuggestions ? 'Getting suggestions...' : 'Get AI suggestions'}
                </button>
              </div>
              <TagInput
                tags={tags}
                availableTags={availableTags}
                onTagsChange={setTags}
                placeholder="Add tag (e.g., strategy, cards, tile-placement...)"
                maxTags={10}
              />
              <p className="text-xs text-muted mt-1">
                Add tags to categorize this game (maximum 10)
              </p>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-body mb-2">
                Description (Optional)
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Brief description of the game..."
              />
            </div>

            {/* Player Metadata Fields */}
            <div>
              <MetadataFieldsEditor
                fields={playerMetadataFields}
                onFieldsChange={setPlayerMetadataFields}
              />
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-edge flex space-x-3">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Updating Game...' : 'Update Game'}
            </button>
            <button
              type="button"
              onClick={() => {
                const returnPath = (location.state as { returnPath?: string })?.returnPath;
                navigate(returnPath || '/games');
              }}
              className="flex-1 flex justify-center py-2 px-4 border border-edge-input rounded-md shadow-sm text-sm font-medium text-body bg-surface hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>

      {/* BGG Search Modal */}
      <BGGSearchModal
        isOpen={bggModalOpen}
        onClose={() => setBggModalOpen(false)}
        onSelect={handleBggSelect}
        initialQuery={name}
      />
    </div>
  );
};

export default GameEditLibraryPage;